/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `MenuNavigation` for the Caintra Connect frontend.
 * @module components/SideMenu/MenuNavigation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { NotificationsNoneRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import styles from "../../styles/SideMenu.module.css";
import { ReactComponent as HomeIcon } from "../../assets/icons/SideMenu/HomeIcon.svg";
import { ReactComponent as DirectoryIcon } from "../../assets/icons/SideMenu/DirectoryIcon.svg";
import { ReactComponent as RequirementsIcon } from "../../assets/icons/SideMenu/RequirementsIcon.svg";
import { ReactComponent as EventsIcon } from "../../assets/icons/SideMenu/EventsIcon.svg";
import { ReactComponent as AttendanceIcon } from "../../assets/icons/SideMenu/AssistanceIcon.svg";
import { ReactComponent as CompaniesIcon } from "../../assets/icons/SideMenu/CompaniesIcon.svg";
import { ReactComponent as CategoriesIcon } from "../../assets/icons/SideMenu/CategoriesIcon.svg";
import { ReactComponent as BuyerCompaniesIcon } from "../../assets/icons/SideMenu/BuyerCompaniesIcon.svg";
import { CONTEXTS, ROUTES } from "./constants";
import {
  canAccessAttendance,
  canAccessBuyerCompanies,
  canAccessCategories,
  canAccessCompanyCatalog,
  canAccessCustomNotifications,
  canAccessDirectory,
  canAccessEvents,
  canAccessReports,
  canAccessRequirements,
} from "../../utils/accessControl";
import CustomizeEventSubmenu from "./CustomizeEventSubmenu";

/** Reusable sidebar navigation item with icon and label. */
const MenuItem = ({ icon: Icon, label, isActive, onClick }) => (
  <div
    className={`${styles.menuItem} ${isActive ? styles.active : ""}`}
    onClick={onClick}
  >
    <Icon className={styles.menuIcon} />
    <span className={styles.menuText}>{label}</span>
  </div>
);

/**
 * Scrollable main navigation section of the sidebar.
 * Renders permission-gated items and the customize-event submenu.
 */
const MenuNavigation = ({
  user,
  permissions,
  location,
  personalizarEventoExpanded,
  setPersonalizarEventoExpanded,
  isMenuItemActive,
  navigateTo,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.menuOptionsScroll}>
      <div className={styles.menuOptions}>
        <MenuItem
          icon={HomeIcon}
          label={t("pages.menu.home")}
          isActive={isMenuItemActive(ROUTES.HOME)}
          onClick={() => navigateTo(ROUTES.HOME)}
        />

        {canAccessDirectory(user, permissions) && (
          <MenuItem
            icon={DirectoryIcon}
            label={t("pages.menu.directory")}
            isActive={isMenuItemActive(ROUTES.DIRECTORY, CONTEXTS.DIRECTORY)}
            onClick={() =>
              navigateTo(ROUTES.DIRECTORY, {
                state: { context: CONTEXTS.DIRECTORY },
              })
            }
          />
        )}

        {canAccessRequirements(user, permissions) && (
          <MenuItem
            icon={RequirementsIcon}
            label={t("pages.menu.requirements")}
            isActive={isMenuItemActive(ROUTES.REQUIREMENTS)}
            onClick={() => navigateTo(ROUTES.REQUIREMENTS)}
          />
        )}

        {canAccessEvents(user, permissions) && (
          <MenuItem
            icon={EventsIcon}
            label={t("pages.menu.events")}
            isActive={isMenuItemActive(ROUTES.EVENTS)}
            onClick={() => navigateTo(ROUTES.EVENTS)}
          />
        )}

        <CustomizeEventSubmenu
          user={user}
          permissions={permissions}
          location={location}
          personalizarEventoExpanded={personalizarEventoExpanded}
          setPersonalizarEventoExpanded={setPersonalizarEventoExpanded}
          isMenuItemActive={isMenuItemActive}
          navigateTo={navigateTo}
        />

        {canAccessAttendance(user, permissions) && (
          <MenuItem
            icon={AttendanceIcon}
            label={t("pages.menu.attendance")}
            isActive={isMenuItemActive(ROUTES.ATTENDANCE)}
            onClick={() => navigateTo(ROUTES.ATTENDANCE)}
          />
        )}

        {canAccessCompanyCatalog(user, permissions) && (
          <MenuItem
            icon={CompaniesIcon}
            label={t("pages.menu.companyCatalog")}
            isActive={isMenuItemActive(
              ROUTES.COMPANY_CATALOG,
              CONTEXTS.COMPANY_CATALOG,
            )}
            onClick={() => navigateTo(ROUTES.COMPANY_CATALOG)}
          />
        )}

        {canAccessCategories(user, permissions) && (
          <MenuItem
            icon={CategoriesIcon}
            label={t("pages.menu.categories")}
            isActive={isMenuItemActive(ROUTES.CATEGORY_CATALOG)}
            onClick={() => navigateTo(ROUTES.CATEGORY_CATALOG)}
          />
        )}

        {canAccessBuyerCompanies(user) && (
          <MenuItem
            icon={BuyerCompaniesIcon}
            label={t("pages.menu.buyerCompanies")}
            isActive={isMenuItemActive(
              ROUTES.BUYER_COMPANIES,
              CONTEXTS.BUYER_COMPANIES,
            )}
            onClick={() => navigateTo(ROUTES.BUYER_COMPANIES)}
          />
        )}

        {canAccessCustomNotifications(user, permissions) && (
          <MenuItem
            icon={NotificationsNoneRounded}
            label={t("pages.menu.notifications")}
            isActive={isMenuItemActive(ROUTES.NOTIFICATION_CENTER)}
            onClick={() => navigateTo(ROUTES.NOTIFICATION_CENTER)}
          />
        )}

        {canAccessReports(user) && (
          <MenuItem
            icon={BuyerCompaniesIcon}
            label={t("pages.menu.reports")}
            isActive={isMenuItemActive(ROUTES.REPORTS)}
            onClick={() => navigateTo(ROUTES.REPORTS)}
          />
        )}
      </div>
    </div>
  );
};

export default MenuNavigation;
